import type { UpToDateSkillsetCourse } from "../type";

const greener3dPrintingPng = new URL(
  "../course_doc/course_image/3D_print_with_AI_or_3D_printing_for_a_greener_future.png",
  import.meta.url,
).href;
const comicFourPanelAiPng = new URL("../course_doc/course_image/AI_in_sustainable_art_and_design.png", import.meta.url).href;
const gameTestAiImage = new URL("../course_doc/course_image/game_test_ai.jpg", import.meta.url).href;
const miniGameDevelopmentImage = new URL("../course_doc/course_image/mini_game_development.jpg", import.meta.url).href;

export const upToDateSkillsetCourses: UpToDateSkillsetCourse[] = [
  {
    id: "ai-sustainable-art-design",
    topic: "用 AI 創作你的漫畫故事",
    title: "4格漫畫AI",
    content:
      "從素材蒐集到提示詞撰寫，學會用 AI 生成角色與場景，最後完成 4 格漫畫排版成品。重點放在「把想像變成可交付作品」：分鏡、對白框、情緒節奏與一致性角色設計。",
    highlights: [
      "把故事拆成 4 格分鏡：起承轉合、鏡頭切換與節奏",
      "提示詞策略：角色一致性、場景延伸、風格固定",
      "把生成圖轉成可讀的版面：對白框、留白、視線流向",
      "學會迭代：用「差異描述」修正畫面與文字",
    ],
    rebrandingFocus: "以「故事表達」為主軸，加入可持續/生活議題的情境設計，練習把抽象想法轉成可理解、可分享的敘事作品。",
    esg: ["以生活/可持續議題為題材，練習用故事呈現觀點與行動建議", "討論 AI 生成素材的來源、版權與署名方式（作品標注規範）"],
    activity:
      "完成 1 組 4 格漫畫（角色、場景、對白框、排版），並產出 1 份創作流程摘要（素材來源、提示詞策略、迭代修正點）。",
    activities: ["設定主題與角色（參考素材蒐集）", "撰寫提示詞並生成角色/場景（保持一致性）", "完成 4 格分鏡與對白", "排版輸出並做 1 次修正版"],
    deliverables: ["1 組 4 格漫畫成品（含對白框與排版）", "1 份流程摘要（素材來源、提示詞策略、迭代修正點）"],
    partner: "AC goose + Bustyle + TF",
    media: {
      kind: "image",
      src: comicFourPanelAiPng,
      alt: "4格漫畫AI 課程單張",
    },
  },
  {
    id: "ai-3d-printing-greener-future",
    topic: "手繪成真：AI × 3D 列印（更綠色的未來）",
    title: "手繪成真 AI × 3D列印",
    content:
      "把手繪想法轉成可列印的 3D 模型：理解 2D → 3D 的轉換概念、基本建模與切片流程，並用 AI 協助你快速探索造型與修正結構，最後列印出實體作品。",
    highlights: [
      "2D → 3D 的轉換思路：輪廓、厚度、結構與連接",
      "基本建模與輸出：尺寸、容差、支撐與切片參數",
      "用 AI 幫你探索造型與快速提出修正方向",
      "以可製造為目標：避免薄弱、易斷、難列印的設計",
    ],
    rebrandingFocus: "在材料與設計上加入「更少浪費」的思考：嘗試可分解/環保材料的選項，並以可重用、可維修的設計觀念完成作品。",
    esg: ["在設計階段思考耐用與可重用（減少一次性列印）", "討論材料選擇與列印失敗的浪費如何降低（支撐、厚度、方向）"],
    activity:
      "以 CAD/建模工具完成 1 個簡單物件（例如鑰匙圈/小容器/徽章），完成切片設定並列印實體；加做 1 次結構或造型迭代（厚度、支撐、連接處等）。",
    activities: ["選定物件與尺寸（用途＋限制）", "完成基礎建模並輸出檔案", "切片設定與列印（方向/支撐/填充）", "依列印結果迭代 1 次（厚度/結構/造型）"],
    deliverables: ["1 個可列印且成功輸出的 3D 作品", "1 次迭代版本（含你修改的原因與效果）"],
    partner: "Bustyle + TF",
    media: {
      kind: "image",
      src: greener3dPrintingPng,
      alt: "AI × 3D列印 課程單張",
    },
  },
  {
    id: "ai-mini-game-development",
    topic: "AI 輔助迷你遊戲開發",
    title: "Mini Game",
    content:
      "用 AI 幫你把遊戲點子落地：拆解玩法、規則、關卡與回饋機制，並學會把需求變成清晰的任務與測試清單。課程聚焦在「可玩的最小版本（MVP）」與迭代。",
    highlights: ["把點子變成規格：玩法、勝負條件、回饋", "用 AI 把需求拆成任務與測試清單", "做出可玩的 MVP，再用數據/感覺迭代手感"],
    activity:
      "完成 1 個可玩的迷你遊戲（基本操作、得分/勝負條件、至少 1 個關卡或變化），並為其中 1 個角色/元素加入簡單 AI 行為（例如追逐、巡邏或判斷）。",
    activities: ["定義玩法與規則（MVP）", "完成操作、得分/勝負條件", "加入 1 個變化（關卡/難度/道具）", "為 1 個元素加入簡單 AI 行為（追逐/巡邏/判斷）"],
    deliverables: ["1 個可玩的迷你遊戲（MVP）", "1 個 AI 行為元素（追逐/巡邏/判斷其中一種）"],
    partner: "AC goose + Bustyle + TF",
    media: {
      kind: "image",
      src: miniGameDevelopmentImage,
      alt: "AI 迷你遊戲開發 課程單張",
    },
  },
  {
    id: "ai-3d-game-development",
    topic: "AI 輔助 3D 遊戲開發",
    title: "Flappy Bird",
    content:
      "用 3D 場景與基礎物理打造經典玩法，理解 3D 物件、碰撞、鏡頭與操控。以 AI 協助你整理關卡節奏與參數調校方法，讓遊戲手感更穩定、可重現。",
    highlights: ["理解 3D 場景、碰撞、鏡頭與操控", "建立可調參數：速度、重力、間距、生成規則", "用調校表把手感迭代「記錄下來」"],
    activity: "完成 1 個 3D 版本的經典玩法（障礙、碰撞、得分、失敗/重開），並完成 1 份調校表記錄你的迭代結果。",
    activities: ["搭建 3D 場景與角色操控", "實作障礙生成、碰撞與得分", "加入失敗/重開流程", "建立參數調校表並完成至少 1 次迭代"],
    deliverables: ["1 個可玩的 3D 經典玩法（含得分與重開）", "1 份參數調校表（速度/重力/間距/生成規則）"],
    partner: "Bustyle + TF",
  },
  {
    id: "ai-board-game-development",
    topic: "AI 輔助桌遊設計",
    title: "桌遊設計工程師",
    content:
      "從主題、機制到平衡性測試，學會桌遊設計的核心流程。用 AI 產生規則草案、卡牌文本與測試情境，並建立一套「可測試、可修改」的規則版本管理方式。",
    highlights: ["機制設計：回合流程、資源、勝利條件", "用 AI 生成規則草案與卡牌文本（再人工校對）", "用測試紀錄做平衡性迭代"],
    activity: "完成 1 個桌遊原型（規則＋卡牌/版圖），並進行至少 1 輪測試與修正。",
    activities: ["選定主題與核心機制", "產出規則草案與卡牌文本", "製作原型並進行測試", "依測試結果修正平衡與規則文字"],
    deliverables: ["1 份可玩的桌遊規則（含回合流程與勝負條件）", "1 組卡牌/版圖原型（可列印或手作）", "1 份測試紀錄（問題點＋修正）"],
    partner: "AC goose + Bustyle + TF",
  },
  {
    id: "cybersecurity-introduction",
    topic: "網絡安全入門",
    title: "Cyber security game 陣營對戰",
    content:
      "以情境遊戲理解常見威脅與防護觀念：密碼與驗證、社交工程、資料外洩、權限與備份。重點是培養「看得懂風險、做得出基本防護」的實用能力。",
    activity: "以分組對戰/角色扮演方式模擬攻防流程，完成 1 份防護清單（帳號、裝置、資料）並演練事件回應步驟。",
    highlights: ["辨識社交工程與釣魚訊息", "建立帳號/裝置/資料的基本防護清單", "事件回應流程：通報、隔離、復原、檢討"],
    activities: ["攻防情境對戰（角色扮演）", "建立個人防護清單（帳號/裝置/資料）", "演練事件回應：通報 → 隔離 → 復原"],
    deliverables: ["1 份個人防護清單（可直接套用）", "1 份事件回應流程小抄（情境演練結果）"],
    partner: "e-walker + TF",
  },
  {
    id: "ai-game-testing-human-in-the-loop",
    topic: "遊戲測試 AI：Human-in-the-loop（Chok Chok Bug）",
    title: "Human in the loop - chok chok bug",
    content:
      "把測試做成可重複的流程：理解開發週期、缺陷分類、重現步驟與優先級。用 AI 起草測試計畫與測試案例，再由人類審核與判斷，建立可靠的品質把關方法。",
    activity:
      "完成 1 份測試計畫（範圍、風險、時程）＋至少 6 條測試案例（含重現步驟與預期結果），並以實測回報缺陷（含嚴重度與建議修正）。",
    highlights: ["測試計畫怎麼寫：範圍、風險、時程", "測試案例：重現步驟、預期結果、實際結果", "缺陷回報：嚴重度、優先級、建議修正"],
    activities: ["用 AI 起草測試計畫並人工審核", "撰寫至少 6 條測試案例", "實測並回報缺陷（含嚴重度/優先級）"],
    deliverables: ["1 份測試計畫", "至少 6 條測試案例", "一份缺陷回報清單（含嚴重度/優先級）"],
    partner: "TF + Bustyle + Ivantito",
    media: {
      kind: "image",
      src: gameTestAiImage,
      alt: "遊戲測試 AI 課程單張",
    },
  },
  {
    id: "data-analysis-visualization",
    topic: "數據分析與視覺化（AI 輔助）",
    title: "年宵前後要分析",
    content:
      "把零散資料變成能說服人的圖表：學會整理、清理、分群與基本指標解讀。用 AI 協助你檢查資料品質、提出分析角度與生成圖表草案，再由你做最後判讀與呈現。",
    activity:
      "以問卷/活動數據做 1 次分析：完成資料表整理、至少 3 張圖表（趨勢/比較/分佈），並用 3–5 句結論解釋「發生了什麼、可能原因、下一步」。",
    highlights: ["資料清理：缺失值、重複、分類一致性", "選對圖表：趨勢/比較/分佈的用法", "用結論回答：發生了什麼 → 為什麼 → 下一步"],
    activities: ["整理並清理資料表", "製作至少 3 張圖表（趨勢/比較/分佈）", "寫出 3–5 句結論（含下一步建議）"],
    deliverables: ["1 份整理後的資料表", "至少 3 張圖表", "1 段結論摘要（3–5 句）"],
    partner: "TF + Tracy (project mgt)",
  },
  {
    id: "ai-powered-storytelling",
    topic: "AI 故事創作與敘事設計",
    title: "AI 演說好拍擋",
    content:
      "練習把想法講得清楚、講得動人：從故事結構、觀眾視角到節奏安排。用 AI 協助你產出提綱、草稿與修辭版本，最後由你選擇最適合的語氣與內容。",
    rebrandingFocus: "以生活/社會/可持續議題為題材，學會把資訊轉成易理解的故事或演說內容。",
    activity:
      "完成 1 篇短篇故事或 1 段 2–3 分鐘演說稿，並完成 1 次「AI 草稿 → 人類改寫」對照：標出你修改的理由（更清楚/更有情緒/更符合受眾）。",
    highlights: ["故事結構：開場鉤子、衝突、轉折、收束", "受眾視角：把資訊說成聽得懂的語言", "AI 草稿 vs 人類改寫：你要能說出修改理由"],
    esg: ["以可持續/社會議題做題材，把資訊轉成能感動人的敘事", "討論 AI 寫作的真實性、引用與透明度（如何標注）"],
    activities: ["產出提綱 → 草稿 → 修辭版本（AI 輔助）", "選定最適合受眾的語氣與結構", "完成 AI 草稿與人類改寫對照"],
    deliverables: ["1 篇短篇故事或 1 段 2–3 分鐘演說稿", "1 份改寫對照（標注修改理由）"],
    partner: "AC goose + Bustyle + TF",
  },
  {
    id: "robotics-transportation",
    topic: "運輸與服務機械人（AI 概念）",
    title: "低空經濟模礙機械車 / AI 送餐program",
    content:
      "理解服務型機械人的基本模組：感測、控制、路徑與任務流程。以簡化情境把「送餐/運輸」拆成可程式化的步驟，並理解安全與限制（避障、停靠、錯誤處理）。",
    activity:
      "以簡易機械車/積木平台完成 1 個任務流程（出發 → 行駛 → 停靠 → 回程），並加入 1 個感測判斷（例如距離/顏色/路徑標記）。",
    highlights: ["把任務拆成流程：狀態機（出發/行駛/停靠/回程）", "感測判斷：距離/顏色/標記與觸發條件", "安全與例外：停車、避障、錯誤處理"],
    activities: ["搭建或設定機械車平台", "完成任務流程（出發→行駛→停靠→回程）", "加入 1 個感測判斷與例外處理"],
    deliverables: ["1 個可重複運行的任務流程程式", "1 個感測判斷（距離/顏色/標記擇一）"],
    partner: "Invantito + TF + bustyle",
  },
  {
    id: "ai-music-composition",
    topic: "AI 音樂創作：作曲／作詞／編曲入門",
    title: "作曲作詞.. AI 音樂療癒好幫手",
    content:
      "用 AI 協助你把旋律與文字拼成完整作品：理解節拍、和弦情緒、段落結構（主歌/副歌/橋段）與基本編曲。重點是把作品做完，而不是只停留在片段。",
    rebrandingFocus: "以特定主題（例如自然、社區、關懷）創作，練習用音樂傳遞訊息與情緒。",
    activity: "完成 1 段 45–90 秒音樂作品（旋律＋至少 1 段歌詞或旁白），並做 1 次版本迭代（節奏/段落/情緒）。",
    highlights: ["段落結構：主歌/副歌/橋段與情緒曲線", "基本和弦與節拍概念（可用 AI 輔助）", "用迭代把作品做完整：版本管理與取捨"],
    esg: ["以自然/社區/關懷為主題創作，練習用聲音傳遞訊息", "討論 AI 音樂的署名、素材來源與使用規範"],
    activities: ["定義主題與情緒", "產出旋律/歌詞草稿（AI 輔助）", "完成 45–90 秒作品並做 1 次迭代"],
    deliverables: ["1 段 45–90 秒音樂作品", "1 次迭代版本（你調整了什麼、為什麼）"],
    partner: "AC goose + TF",
  },
  {
    id: "ai-ethics",
    topic: "AI 倫理與社會影響",
    title: "講AI AI講",
    content:
      "用真實案例理解 AI 的偏見、隱私、著作權、透明度與責任問題。課程重點是學會提出「好問題」與做出「可辯護的選擇」，而不是背答案。",
    activity:
      "以案例討論/辯論方式完成 1 份立場短文：指出風險、受影響者、你採取的規範或做法，以及你為什麼這樣選。",
    highlights: ["偏見與公平：誰會被影響？", "隱私與同意：資料從哪來、能不能用？", "著作權與透明度：要怎麼標注與負責？"],
    activities: ["閱讀案例並分組討論", "用一套評估表列出風險與受影響者", "完成立場短文並互評"],
    deliverables: ["1 份立場短文（含風險、受影響者、規範與理由）", "1 份案例評估表（可套用到其他案例）"],
    partner: "School + TF",
  },
  {
    id: "cross-culture-3d-music-box",
    topic: "跨文化 3D 音樂盒創作",
    title: "3D 文化製作音樂盒",
    content:
      "把文化元素做成可看、可摸、可聽的作品：以 3D 結構承載圖騰/符號，並配合旋律或機械結構形成「可運作」的音樂盒概念。重點是故事與形式一致。",
    highlights: ["把文化符號轉成造型語言（圖騰→結構）", "結構可運作：組裝、耐用、可展示", "聲音/旋律如何支撐故事與主題"],
    activity: "完成 1 個文化主題音樂盒概念原型（外觀＋結構），並寫出作品敘事說明。",
    activities: ["選定文化主題與故事", "完成外觀/結構設計（可 3D 列印或手作原型）", "寫出敘事說明並展示"],
    deliverables: ["1 個音樂盒概念原型（外觀＋結構）", "1 份作品敘事說明（主題、符號、故事）"],
    partner: "Bustyle + TF",
  },
];

export const getUpToDateSkillsetCoursePath = (courseId: string) => `/courseDetails/up-to-date-skillset/${courseId}`;
